/* @flow */
import { fromJS } from 'immutable';
import type { List } from 'immutable';
import { saveTextToFile, toUpper } from 'trustar/utils';
import { tryCatch } from 'trustar/fp/Either';
import createDebugger from 'debug';
const debug = createDebugger('app:modules:redaction:actions');

import { sanitizeCsvFields } from '../../utils/csv';

import { categoryByName, categoryById } from './constants.js';
import type { Term } from './types.js';

/**
 * TODO: What should the default value be?
 * What should we return if there is an error getting a cat ID?
 */
const getCategoryIdByType = (type) => {
  let result = null;

  try {
    switch (type) {
    case 'EMAIL':
      result = categoryByName('Email Address').get('id');
      break;
    case 'PERSON':
      result = categoryByName('Person').get('id');
      break;
    case 'PHONE':
      result = categoryByName('Phone').get('id');
      break;
    case 'CREDIT_CARD':
      result = categoryByName('Account Number').get('id');
      break;
    case 'IP':
      result = categoryByName('IP Address').get('id');
      break;
    case 'ORGANIZATION':
      result = categoryByName('Organization').get('id');
      break;
    case 'LOCATION':
      result = categoryByName('Location').get('id');
      break;
    case 'URL':
      result = categoryByName('URL').get('id');
      break;
    case 'SSN':
      result = categoryByName('Social Security Number').get('id');
      break;
    case 'PASSWORD':
    default:
      result = categoryByName('Other Identifying Info').get('id');
    }
  } catch (err) {
    debug(`Could not find category for type ${type}`, err);
  }

  return result;
};

const categoryNameByTerm = (term: Term): string => {
  return toUpper(categoryById(term.get('categoryId')).get('name'));
};

/**
 * Converts the redaction terms into a CSV where the first column is the text
 * and the second column is the capitalized category name.
 */
const convertTermsToCsv = (terms: List<Term>): string => {
  return terms.map(term => `${term.get('text')},${categoryNameByTerm(term)}`).join('\n');
};

/**
 * IMPORTANT: The types that come down on redactables don't map 1-to-1 to our
 * categories and a category is necessary to actually redact something. As such
 * we use the type to infer a category.
 *
 * NOTE: This is fragile. If the names of the categories were to change this
 * would no longer work.
 */
export const mapRedactables = (redactables: any) => {
  return fromJS(redactables).map((v, k) => {
    const category = v.get('category');
    return v.set('categoryId', getCategoryIdByType(category));
  });
};

export const validateTerm = ({ text, type, categoryId }: any, exists: boolean) => {
  if (!type) return 'Invalid redaction type.';
  if (!categoryId) return 'Invalid category.';
  if (text.trim().length < 2) {
    return 'Redaction terms must be at least 2 characters long.';
  }

  if (exists) {
    return 'Term already exists. NOTE: Terms are not case sensitive.';
  }

  // If we return null then that means validation passed
  return null;
};

/* Export a map of redaction terms to a JSON file */
export const exportRedactionLibrary = (terms: List<Term>, format: string) => {

  const exportFileName = 'RedactionTerms.' + format;

  switch (format) {
  case 'json':
    terms = terms.map(term => {
      return fromJS({
        text: term.get('text'),
        category: categoryNameByTerm(term)
      });
    });
    try {
      const termsJson = JSON.stringify(terms, null, 2);
      debug(saveTextToFile(exportFileName, termsJson));
    }
    catch(err) {
      debug(`error exporting redaction library to json`);
    }
    break;
  case 'csv':
    try {
      const termsCsv = convertTermsToCsv(terms);
      const sanitizedCsv = sanitizeCsvFields(termsCsv);
      debug(saveTextToFile(exportFileName, sanitizedCsv));
    }
    catch(err) {
      debug(`error exporting redaction library csv`);
    }
    break;
  default:
    debug(`Invalid format for exporting redaction library: ${format}`);
    break;
  }
};



// WEBPACK FOOTER //
// ./src/app/modules/redaction/utils.js